import { useEffect, useState, useMemo } from 'react';
import {
  Card,
  Form,
  Select,
  DatePicker,
  Input,
  Button,
  Alert,
  Space,
  Typography,
  Empty,
  Tag,
  Divider,
} from 'antd';
import { ExperimentOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  DermatoscopyResult,
  Device,
  type CreateDermatoscopyDto,
  type DermatoscopyExam,
} from '../../../../api/dermatoApi';
import { useAuthStore } from '../../../../store/authStore';
import { useDermatoWizard, useRegisterStepSave } from '../DermatoWizardContext';
import dermatoApi from '../../../../api/dermatoApi';

const { Text } = Typography;

const RESULT_OPTIONS = Object.values(DermatoscopyResult).map((v) => ({
  value: v,
  label:
    v === 'NO_SUSPICIOUS_FEATURES'
      ? 'Fără trăsături suspecte'
      : v === 'SUSPICIOUS_LESION'
        ? 'Leziune suspectă'
        : v === 'REQUIRES_FURTHER_EVALUATION'
          ? 'Necesită evaluări suplimentare'
          : 'Neconcludent',
}));

const DEVICE_OPTIONS = Object.values(Device).map((v) => ({
  value: v,
  label:
    v === 'DERM_LITE_DL5'
      ? 'DermLite DL5'
      : v === 'HEINE_DELTA_30'
        ? 'Heine Delta 30'
        : v === 'DERM_LITE_HUHR_U'
          ? 'DermLite HÜHR u'
          : v === 'FOTOFINDER_ATBM'
            ? 'FotoFinder ATBM'
            : 'Altul',
}));

const Step6Dermatoscopy = () => {
  const { lesions, selectedLesionId, selectLesion, loading } = useDermatoWizard();
  const { user } = useAuthStore();
  const [formAntd] = Form.useForm<CreateDermatoscopyDto>();
  const [exam, setExam] = useState<DermatoscopyExam | null>(null);
  const [loadingExam, setLoadingExam] = useState(false);
  const [saving, setSaving] = useState(false);

  const lesion = useMemo(
    () => lesions.find((l) => l.id === selectedLesionId) ?? null,
    [lesions, selectedLesionId]
  );

  useEffect(() => {
    if (!lesion?.id) {
      setExam(null);
      return;
    }
    let cancelled = false;
    const load = async () => {
      try {
        setLoadingExam(true);
        const v = await dermatoApi.dermatoscopy.getByLesion(lesion.id!);
        if (cancelled) return;
        setExam(v);
        if (v) {
          formAntd.setFieldsValue({ ...v, examDate: dayjs(v.examDate) } as any);
        } else {
          formAntd.resetFields();
          formAntd.setFieldsValue({
            examDate: dayjs(),
            performedBy: `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim() || user?.email || '',
            device: Device.DERM_LITE_DL5,
            result: DermatoscopyResult.NO_SUSPICIOUS_FEATURES,
          } as any);
        }
      } catch {
        if (!cancelled) setExam(null);
      } finally {
        if (!cancelled) setLoadingExam(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [lesion?.id, formAntd, user]);

  const persist = async (): Promise<boolean> => {
    if (!lesion?.id) return true;
    try {
      const values = await formAntd.validateFields();
      setSaving(true);
      const dto: CreateDermatoscopyDto = {
        ...values,
        examDate: (values.examDate as unknown as dayjs.Dayjs).toISOString(),
      };
      const saved = exam?.id
        ? await dermatoApi.dermatoscopy.update(exam.id, dto)
        : await dermatoApi.dermatoscopy.createForLesion(lesion.id, dto);
      setExam(saved);
      return true;
    } catch {
      // validation
      return false;
    } finally {
      setSaving(false);
    }
  };

  useRegisterStepSave(persist);

  if (loading) {
    return <Card loading title="Dermatoscopie" />;
  }

  if (lesions.length === 0) {
    return (
      <Card title="Dermatoscopie">
        <Empty description="Nu există leziuni definite. Mergi la pasul 3." />
      </Card>
    );
  }

  return (
    <Card
      title={
        <Space>
          <ExperimentOutlined style={{ color: '#1890ff' }} />
          <span>Dermatoscopie — per leziune</span>
        </Space>
      }
    >
      <Form.Item label="Selectează leziunea" required style={{ marginBottom: 16 }}>
        <Select
          value={selectedLesionId ?? undefined}
          onChange={selectLesion}
          placeholder="Alege o leziune din inventar"
          options={lesions.map((l) => ({
            value: l.id,
            label: `${l.clinicalCode} — ${l.location}`,
          }))}
        />
      </Form.Item>

      {loadingExam && <Alert type="info" message="Se încarcă examinarea dermatoscopică…" />}

      {!lesion && !loadingExam && (
        <Alert type="warning" showIcon message="Selectează o leziune pentru a continua." />
      )}

      {lesion && !loadingExam && (
        <>
          <Alert
            type="info"
            showIcon
            message={
              <Space>
                <Text>Leziune:</Text>
                <Tag color="blue">{lesion.clinicalCode}</Tag>
                <Text strong>{lesion.location}</Text>
              </Space>
            }
            style={{ marginBottom: 16 }}
          />

          <Form form={formAntd} layout="vertical">
            <Form.Item name="examDate" label="Data examinării" rules={[{ required: true }]}>
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="performedBy" label="Medic" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
            <Form.Item name="device" label="Dispozitiv" rules={[{ required: true }]}>
              <Select options={DEVICE_OPTIONS} />
            </Form.Item>
            <Form.Item name="description" label="Descriere dermatoscopică" rules={[{ required: true }]}>
              <Input.TextArea rows={4} placeholder="Pattern observat, structuri pigmentare, vascularizație…" />
            </Form.Item>
            <Form.Item name="result" label="Rezultat" rules={[{ required: true }]}>
              <Select options={RESULT_OPTIONS} />
            </Form.Item>
            <Form.Item name="recommendation" label="Recomandare">
              <Input.TextArea rows={2} placeholder="ex: monitorizare 3 luni, biopsie, re-examinare 6 luni…" />
            </Form.Item>

            <Divider />

            <Form.Item>
              <Space>
                <Button type="primary" onClick={() => void persist()} loading={saving}>
                  {exam ? 'Actualizează' : 'Salvează dermatoscopia'}
                </Button>
                {exam && (
                  <Text type="success">✓ Examinare existentă — {exam.id.slice(0, 8)}</Text>
                )}
              </Space>
            </Form.Item>
          </Form>
        </>
      )}
    </Card>
  );
};

export default Step6Dermatoscopy;
